"use client";

import { useEffect, useState } from "react";

import { useTranslations } from "next-intl";

import { getLocalStorage, setLocalStorage } from "@/shared/lib/cookieConsent";
import { Typography } from "@/shared/ui";

type WakeTimeSelectProps = {
    onTimeChange: (hour: number, minute: number) => void;
};

const STORAGE_KEY = "wake-time-preferences";

export const WakeTimeSelect = ({ onTimeChange }: WakeTimeSelectProps) => {
    const t = useTranslations("WakeTimeSelect");
    const [selectedHour, setSelectedHour] = useState(7);
    const [selectedMinute, setSelectedMinute] = useState(0);

    const hours = Array.from({ length: 5 }, (_, i) => i + 5); // 5 to 9
    const minutes = Array.from({ length: 4 }, (_, i) => i * 15); // 0 to 45 by 15

    const selectClassName =
        "px-4 py-2 rounded-lg border border-primary-100 bg-white text-black focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-transparent";

    useEffect(() => {
        // Load saved preferences
        try {
            const savedPrefs = getLocalStorage(STORAGE_KEY);
            if (savedPrefs) {
                const { hour, minute } = JSON.parse(savedPrefs);
                setSelectedHour(hour);
                setSelectedMinute(minute);
                onTimeChange(hour, minute);
            }
        } catch (error) {
            console.error(t("errorLoadingPreferences"), error);
        }
    }, [onTimeChange, t]);

    const savePreferences = (hour: number, minute: number) => {
        try {
            setLocalStorage(STORAGE_KEY, JSON.stringify({ hour, minute }));
        } catch (error) {
            console.error(t("errorSavingPreferences"), error);
        }
    };

    const handleHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const hour = Number(e.target.value);
        setSelectedHour(hour);
        onTimeChange(hour, selectedMinute);
        savePreferences(hour, selectedMinute);
    };

    const handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const minute = Number(e.target.value);
        setSelectedMinute(minute);
        onTimeChange(selectedHour, minute);
        savePreferences(selectedHour, minute);
    };

    return (
        <>
            <Typography variant="body">{t("selectWakeUpTime")}</Typography>
            <div className="flex gap-2 items-center mt-4 mb-6">
                <select className={selectClassName} onChange={handleHourChange} value={selectedHour}>
                    {hours.map((hour) => (
                        <option key={hour} value={hour}>
                            {hour.toString().padStart(2, "0")}
                        </option>
                    ))}
                </select>
                <span className="text-black font-medium">:</span>
                <select className={selectClassName} onChange={handleMinuteChange} value={selectedMinute}>
                    {minutes.map((minute) => (
                        <option key={minute} value={minute}>
                            {minute.toString().padStart(2, "0")}
                        </option>
                    ))}
                </select>
            </div>
        </>
    );
};
